import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { VerbatimRespondent } from '../../data/verbatimData';
import { assignSegment } from '../../lib/segmentAssignment';

const SEGMENT_STYLES: Record<string, { bg: string; text: string; label: string }> = {
 'always-none': { bg: 'bg-metric-negative/15', text: 'text-metric-negative', label: 'Low-Interest' },
 'never-none': { bg: 'bg-chart-teal/15', text: 'text-chart-teal', label: 'Price-Sensitive' },
 'selective': { bg: 'bg-chart-amber/15', text: 'text-chart-amber', label: 'Selective' },
};

function QuoteBlock({ label, text }: { label: string; text: string }) {
 if (!text) return null;
 return (
 <div className="mb-3">
 <div className="text-xs font-semibold uppercase tracking-wider text-muted mb-1">{label}</div>
 <p className="text-sm leading-relaxed text-primary border-l-2 border-chart-teal/30 pl-3 italic">
 "{text}"
 </p>
 </div>
 );
}

function PriceBar({ distribution }: { distribution: Record<string, number> }) {
 const total = Object.values(distribution).reduce((a, b) => a + b, 0);
 if (total === 0) return null;
 const prices = ['499', '999', '1499', '1699', '1899'];
 const colors = ['#14B8A6', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444'];
 return (
 <div className="flex h-2 rounded-full overflow-hidden bg-surface" title="Price distribution of chosen products">
 {prices.map((p, i) => {
 const w = (distribution[p] || 0) / total * 100;
 if (w === 0) return null;
 return <div key={p} style={{ width: `${w}%`, backgroundColor: colors[i] }} />;
 })}
 </div>
 );
}

function topRankedFeature(r: VerbatimRespondent): string {
 let best = '';
 let bestRank = 99;
 for (const [f, rank] of Object.entries(r.featureRanking)) {
 if (rank > 0 && rank < bestRank) { bestRank = rank; best = f; }
 }
 return best ? `${best} (#${bestRank})` : '';
}

interface Props {
 respondent: VerbatimRespondent;
}

export function VerbatimCard({ respondent: r }: Props) {
 const [profileOpen, setProfileOpen] = useState(false);
 const seg = SEGMENT_STYLES[r.segment];
 const kmeansLabel = assignSegment(r);
 const productCount = 15 - r.noneCount;

 const topFeatures = Object.entries(r.featureChoiceRates)
 .filter(([, rate]) => rate > 0)
 .sort((a, b) => b[1] - a[1])
 .slice(0, 3);

 const profileSummary = [
 r.gender,
 r.age > 0 ? String(r.age) : '',
 r.income ? r.income.split(' - ')[0] : '',
 r.region,
 ].filter(Boolean).join(', ');

 const profileDetail = [
 r.monthlySpend ? `Spends: ${r.monthlySpend.replace('/month', '/mo')}` : '',
 r.primaryGoal ? `Goal: ${r.primaryGoal.length > 35 ? r.primaryGoal.slice(0, 32) + '...' : r.primaryGoal}` : '',
 ].filter(Boolean);

 return (
 <div className="card flex flex-col h-full">
 <div className="flex items-center justify-between mb-2">
 <span className={`px-2 py-0.5 rounded-full text-micro font-semibold ${seg.bg} ${seg.text}`}>
 {kmeansLabel === 'Budget Seekers' ? 'Budget Seeker' : kmeansLabel === 'Low-Interest Prospects' ? 'Low-Interest' : 'Price-Sensitive'}
 </span>
 <span className="text-micro text-muted font-mono">
 {r.noneCount}/15 none
 </span>
 </div>

 {profileSummary && (
 <button
 onClick={() => setProfileOpen(!profileOpen)}
 className="flex items-center gap-1 text-micro text-muted hover:text-secondary mb-2 text-left transition-colors"
 >
 {profileOpen ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
 <span className="truncate">{profileSummary}</span>
 </button>
 )}

 {profileOpen && (
 <div className="text-micro text-secondary bg-surface rounded px-2 py-1.5 mb-2 space-y-0.5">
 <div>{r.gender}, {r.age}, {r.income}, {r.region}</div>
 {profileDetail.map((line, i) => <div key={i}>{line}</div>)}
 {r.purchaseApproach && <div>Approach: {r.purchaseApproach}</div>}
 {topRankedFeature(r) && <div>Top stated feature: {topRankedFeature(r)}</div>}
 </div>
 )}

 <div className="flex-1 min-h-0">
 <QuoteBlock label="Health Goals" text={r.goals} />
 <QuoteBlock label="What Would Make Them Pay" text={r.wtpFactors} />
 {r.priceObjection && <QuoteBlock label="Price Reaction" text={r.priceObjection} />}
 <QuoteBlock label="AlterMe Brand" text={r.brandPerception} />
 </div>

 <div className="mt-3 pt-3 border-t border-border space-y-2">
 <div className="flex items-center justify-between text-detail text-muted">
 <span>Products chosen: <span className="font-semibold text-secondary">{productCount}/15</span></span>
 {r.avgChosenPrice !== null && (
 <span>Avg price: <span className="font-semibold text-secondary">${r.avgChosenPrice.toLocaleString()}</span></span>
 )}
 </div>

 {productCount > 0 && <PriceBar distribution={r.priceDistribution} />}

 {topFeatures.length > 0 && (
 <div className="flex flex-wrap gap-1">
 {topFeatures.map(([name, rate]) => (
 <span
 key={name}
 className="px-1.5 py-0.5 rounded text-micro bg-chart-teal/10 text-chart-teal font-medium"
 title={`${name}: chosen ${Math.round(rate * 100)}% of the time when available`}
 >
 {name.split(' ')[0]} {Math.round(rate * 100)}%
 </span>
 ))}
 </div>
 )}
 </div>
 </div>
 );
}
